import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { TRIP_PURPOSE_LABELS, TRIP_PURPOSES, type LogbookGap, type Trip } from '@kontor/shared';
import { api } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { SortTh } from '../../components/SortTh';
import { errorText, formatDay, kmText } from './shared';
import './fleet.css';

/** Lücke oder Überschneidung zwischen zwei Fahrten als eigene Zeile */
function GapRow({ gap }: { gap: LogbookGap }) {
  return (
    <tr className="fleet-gaprow">
      <td colSpan={8}>
        <strong className="fleet-warn">{gap.kind === 'gap' ? `Lücke: ${kmText(gap.km)} nicht erfasst` : `Überschneidung: ${kmText(gap.km)}`}</strong>{' '}
        <span className="muted small">{kmText(gap.fromKm)} bis {kmText(gap.toKm)}. {gap.kind === 'gap' ? 'Tragen Sie die fehlenden Fahrten nach, damit das Fahrtenbuch lückenlos ist.' : 'Die Kilometerstände der beiden Fahrten überlappen sich, bitte prüfen.'}</span>
      </td>
    </tr>
  );
}

/**
 * Tabelle der Fahrten eines Fahrzeugs. Änderungen am Fahrtenbuch tragen den Vermerk „geändert“, Einträge aus Fahrten in Wirkia Drivers den Vermerk „automatisch“. `gapsBefore` nennt je Fahrt die Lücke oder
 * Überschneidung, die vor ihr steht (die Liste läuft vom neuesten zum ältesten Eintrag), und zeigt sie als eigene Zeile.
 */
export function TripTable({ trips, loading, sort, onSort, onEdit, onDelete, gapsBefore }: { trips: Trip[]; loading?: boolean; sort?: string; onSort?: (s: string) => void; onEdit?: (t: Trip) => void; onDelete?: (t: Trip) => void; gapsBefore?: Map<string, LogbookGap> }) {
  return (
    <div className="card table-card">
      <table className="table table--cards">
        <thead>
          <tr>
            {onSort ? <SortTh label="Datum" sortKey="tripDate" sort={sort ?? ''} onSort={onSort} first="desc" /> : <th>Datum</th>}
            {onSort ? <SortTh label="Fahrzeug" sortKey="vehicle" sort={sort ?? ''} onSort={onSort} /> : <th>Fahrzeug</th>}
            <th>Strecke</th>
            <th>Zweck</th>
            {onSort ? <SortTh label="Kilometer" sortKey="km" sort={sort ?? ''} onSort={onSort} first="desc" className="num" /> : <th className="num">Kilometer</th>}
            <th>Fahrer</th>
            {(onEdit || onDelete) && <th />}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
          {!loading && trips.length === 0 && <tr><td colSpan={8} className="empty">Keine Fahrten.</td></tr>}
          {trips.map((t) => (
            <Fragment key={t.id}>
            {gapsBefore?.get(t.id) && <GapRow gap={gapsBefore.get(t.id)!} />}
            <ExpandRow
              summary={<Summary title={`${t.fromPlace} → ${t.toPlace}`} aside={<strong>{kmText(t.distanceKm)}</strong>} line2={`${formatDay(t.tripDate)} · ${t.vehiclePlate} · ${TRIP_PURPOSE_LABELS[t.purpose]}`} line3={[t.source === 'ride' ? 'automatisch aus Fahrt' : '', t.edited ? 'nachträglich geändert' : ''].filter(Boolean).join(' · ') || undefined} />}
            >
              <td className="cell-desktop-title">{formatDay(t.tripDate)}{t.edited && <div className="fleet-warn small">geändert</div>}{t.source === 'ride' && <div className="muted small">automatisch</div>}</td>
              <td data-label="Fahrzeug"><Link to={`/flotte/${t.vehicleId}`}>{t.vehiclePlate}</Link></td>
              <td data-label="Strecke">{t.fromPlace} → {t.toPlace}<div className="muted small">{kmText(t.startKm)} bis {kmText(t.endKm)}</div></td>
              <td data-label="Zweck">{TRIP_PURPOSE_LABELS[t.purpose]}{(t.reason || t.contactName) && <div className="muted small">{[t.reason, t.contactName].filter(Boolean).join(' · ')}</div>}</td>
              <td data-label="Kilometer" className="num">{kmText(t.distanceKm)}</td>
              <td data-label="Fahrer">{t.driverName ?? '–'}</td>
              {(onEdit || onDelete) && (
                <td className="doc-actions">
                  {onEdit && <button type="button" className="btn btn--secondary btn--sm" onClick={() => onEdit(t)}>Ändern</button>}
                  {onDelete && <button type="button" className="btn btn--danger btn--sm" onClick={() => onDelete(t)}>Löschen</button>}
                </td>
              )}
            </ExpandRow>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Fahrt ändern: Strecke, Kilometer, Zweck. Die Änderung bleibt im Fahrtenbuch sichtbar. */
export function TripEdit({ trip, onClose, onSaved }: { trip: Trip; onClose: () => void; onSaved: () => void }) {
  const [from, setFrom] = useState(trip.fromPlace);
  const [to, setTo] = useState(trip.toPlace);
  const [start, setStart] = useState(String(trip.startKm));
  const [end, setEnd] = useState(String(trip.endKm));
  const [purpose, setPurpose] = useState(trip.purpose);
  const [reason, setReason] = useState(trip.reason ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('PATCH', `/fleet/trips/${trip.id}`, { fromPlace: from.trim(), toPlace: to.trim(), startKm: Number(start), endKm: Number(end), purpose, reason: reason.trim() || null }),
    onSuccess: onSaved,
    onError: (e) => setError(errorText(e, 'Das Speichern hat nicht geklappt.')),
  });
  return (
    <form className="card fleet-form" onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate(); }} aria-label="Fahrt ändern">
      <h2>Fahrt vom {formatDay(trip.tripDate)} ändern</h2>
      <p className="muted small">Eine Änderung bleibt im Fahrtenbuch sichtbar (Vermerk „geändert“) und steht mit altem und neuem Wert im Protokoll. Das Finanzamt verlangt, dass nachträgliche Änderungen erkennbar sind.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field"><span>Von</span><input className="input" value={from} onChange={(e) => setFrom(e.target.value)} maxLength={150} required /></label>
        <label className="field"><span>Nach</span><input className="input" value={to} onChange={(e) => setTo(e.target.value)} maxLength={150} required /></label>
        <label className="field"><span>Kilometerstand Beginn</span><input className="input" inputMode="numeric" value={start} onChange={(e) => setStart(e.target.value)} required /></label>
        <label className="field"><span>Kilometerstand Ende</span><input className="input" inputMode="numeric" value={end} onChange={(e) => setEnd(e.target.value)} required /></label>
        <label className="field">
          <span>Zweck</span>
          <select className="input" value={purpose} onChange={(e) => setPurpose(e.target.value as Trip['purpose'])}>{TRIP_PURPOSES.map((p) => <option key={p} value={p}>{TRIP_PURPOSE_LABELS[p]}</option>)}</select>
        </label>
        <label className="field"><span>Reiseanlass</span><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} /></label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>Speichern</button>
        <button type="button" className="btn btn--secondary" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}
